import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "../components/layout";
import { Button, Card, StatusIndicator, type StatusTone } from "../components/ui";
import { ApiUnavailableState, EmptyState, LoadingState } from "../components/states";
import { useAppContext } from "../hooks/useAppContext";
import {
  api,
  apiConfigured,
  ApiError,
  type SupportCaseDetail,
  type SupportCaseState,
  type SupportCaseView,
  type SupportCategoryOption,
  type SupportMessageView,
} from "../lib/api";
import { formatDateNumeric } from "../lib/format";

/**
 * ODIN Support (Part 10.1) — the customer's way to reach a human without leaving
 * the product, and the replacement for "email post@okmedia.live and hope".
 *
 * WHAT THIS SCREEN PROMISES, AND WHY EACH PROMISE IS KEPT
 *
 *  - "We received this." Said only after the server has committed the case. The
 *    confirmation is the server's response, not an optimistic local state flip, so
 *    the screen can never claim a message was received that was in fact lost.
 *
 *  - "Here is what happens next." `deliveryHint`, `stateLabel` and `stateHint` are
 *    all written server-side. This file composes no reassurance of its own: whether
 *    a message reached OKMedia is a fact the server knows, and inventing calming
 *    copy in the browser is exactly the failure Part 10.7 forbids.
 *
 *  - "Nobody else can read this." Enforced on the server, which resolves the case
 *    against the caller's canonical account. Nothing here sends a case id belonging
 *    to another workspace anywhere useful.
 *
 * WHAT IS DELIBERATELY NOT HERE: no Slack workspace URL, channel name, thread id or
 * "join our Slack" invitation — OKMedia's internal workspace is not a place
 * customers are sent (Part 10.2, Part 10.3). No priority selector: a customer who
 * can set priority is a customer who has to guess, and everyone guesses "urgent".
 * No file upload yet — the honest position is that evidence upload is not wired,
 * so the screen does not show a control that would quietly discard a file.
 */

// Categories that mean money. Used only to warn the customer, in advance, that a
// conversation is not itself a refund — the request is a separate, explicit action
// with its own consequences screen (Part 8).
const MONEY_CATEGORIES = new Set(["REFUND", "PAYMENT_FAILURE", "CHANGE_PLAN", "CANCEL_PLAN", "BILLING", "INVOICE"]);

/**
 * Topics a `/app/support?topic=…` link may name (Part 10.2). This is a browser-side
 * allowlist, not an authority: the server validates the category on submit regardless.
 * Its job is narrower — an unrecognised value must be ignored rather than dropped into
 * component state, so a crafted link cannot put arbitrary text into the form.
 */
const KNOWN_TOPICS = new Set([
  "ACCOUNT_LOGIN",
  "DISCORD_CONNECTION",
  "BOT_PERMISSIONS",
  "WORKSPACE_MANAGEMENT",
  "BALDER",
  "BILLING",
  "CHANGE_PLAN",
  "CANCEL_PLAN",
  "REFUND",
  "PAYMENT_FAILURE",
  "INVOICE",
  "SECURITY_CONCERN",
  "OTHER",
]);

const SUBJECT_MAX = 200;
const BODY_MAX = 8000;

export function caseTone(state: SupportCaseState): StatusTone {
  switch (state) {
    case "OPEN":
    case "AWAITING_SUPPORT":
      return "warn";
    case "AWAITING_CUSTOMER":
      return "neutral";
    case "RESOLVED":
    case "CLOSED":
      return "ok";
  }
}

/**
 * "2 hours ago" beats a timestamp for the one question this page exists to answer:
 * has anyone looked at this yet. Falls back to the absolute date once a conversation
 * is old enough that elapsed time stops being the useful framing.
 */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const seconds = Math.round((now - then) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${String(minutes)} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${String(hours)} ${hours === 1 ? "hour" : "hours"} ago`;
  const days = Math.round(hours / 24);
  if (days <= 7) return `${String(days)} ${days === 1 ? "day" : "days"} ago`;
  return formatDateNumeric(new Date(then));
}

function authorLabel(message: SupportMessageView): string {
  switch (message.authorKind) {
    case "CUSTOMER":
      return message.mine ? "You" : "Your workspace";
    case "SUPPORT":
      return "ODIN Support";
    case "SYSTEM":
      return "ODIN";
  }
}

export function MessageBubble({ message }: { message: SupportMessageView }): JSX.Element {
  return (
    <article className={message.mine ? "support-msg support-msg--mine" : "support-msg"}>
      <header className="support-msg__meta">
        <strong>{authorLabel(message)}</strong>
        <span className="support-msg__time">{relativeTime(message.createdAtIso)}</span>
      </header>
      {/* Rendered as text. React escapes it, and nothing on this page interprets a
          customer's or an agent's message as markup. */}
      <p className="support-msg__body">{message.body}</p>
    </article>
  );
}

export function CaseSummary({
  supportCase,
  onOpen,
  selected,
}: {
  supportCase: SupportCaseView;
  onOpen: (id: string) => void;
  selected: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      className={selected ? "support-case support-case--selected" : "support-case"}
      onClick={() => onOpen(supportCase.id)}
      aria-current={selected ? "true" : undefined}
    >
      <span className="support-case__row">
        <StatusIndicator tone={caseTone(supportCase.state)} label={supportCase.stateLabel} />
        <span className="support-case__ref">{supportCase.reference}</span>
      </span>
      <span className="support-case__subject">{supportCase.subject}</span>
      <span className="support-case__row">
        <span className="support-case__cat">{supportCase.categoryLabel}</span>
        <span className="support-case__time">Last reply {relativeTime(supportCase.lastMessageAtIso)}</span>
      </span>
    </button>
  );
}

type ListState =
  | { kind: "loading" }
  | { kind: "ready"; cases: SupportCaseView[] }
  | { kind: "unavailable"; detail?: string };

export function Support(): JSX.Element {
  const { tenantId } = useAppContext();
  const [searchParams] = useSearchParams();
  const [list, setList] = useState<ListState>({ kind: "loading" });
  const [categories, setCategories] = useState<SupportCategoryOption[]>([]);
  const [showClosed, setShowClosed] = useState(false);
  const [detail, setDetail] = useState<SupportCaseDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [composing, setComposing] = useState(false);

  const [category, setCategory] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [topicApplied, setTopicApplied] = useState(false);

  const loadCases = useCallback(() => {
    if (!apiConfigured || !tenantId) return;
    setList({ kind: "loading" });
    api.support
      .cases(tenantId)
      .then((res) => setList({ kind: "ready", cases: res.cases }))
      .catch((error: unknown) =>
        setList({ kind: "unavailable", detail: error instanceof Error ? error.message : undefined }),
      );
  }, [tenantId]);

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  useEffect(() => {
    if (!apiConfigured || !tenantId) return;
    // A missing category list must not block someone from asking for help, so the
    // failure is quiet and the form falls back to the one category that always
    // applies.
    api.support
      .categories(tenantId)
      .then((res) => setCategories(res.categories))
      .catch(() => setCategories([]));
  }, [tenantId]);

  /**
   * A support link elsewhere in the product can say what it is about
   * (`/app/support?topic=REFUND`), which spares the customer re-explaining where they
   * came from. It opens the composer with that topic chosen and NOTHING else: no case
   * is created, no subject or message is invented on their behalf, and nothing is sent
   * until they write it and press send. Applied once, so returning to the list later
   * in the same visit does not keep re-opening the form under them.
   */
  useEffect(() => {
    if (topicApplied) return;
    const requested = searchParams.get("topic");
    if (requested === null) return;
    setTopicApplied(true);
    if (!KNOWN_TOPICS.has(requested)) return;
    setCategory(requested);
    setComposing(true);
  }, [searchParams, topicApplied]);

  const openCase = useCallback(
    async (caseId: string) => {
      if (!tenantId) return;
      setComposing(false);
      setDetailLoading(true);
      setNotice(null);
      setFormError(null);
      try {
        setDetail(await api.support.case(tenantId, caseId));
      } catch (error) {
        setDetail(null);
        setFormError(
          error instanceof ApiError ? error.message : "We could not open that conversation just now.",
        );
      } finally {
        setDetailLoading(false);
      }
    },
    [tenantId],
  );

  const submitNewCase = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!tenantId) return;
      setBusy(true);
      setFormError(null);
      try {
        const created = await api.support.openCase(tenantId, {
          category: category === "" ? "OTHER" : category,
          subject: subject.trim(),
          body: body.trim(),
        });
        // Only now. The confirmation below is the server's answer, never an
        // optimistic guess, so this screen cannot tell somebody we have their
        // message when we do not.
        setDetail(created);
        setComposing(false);
        setSubject("");
        setBody("");
        setCategory("");
        setNotice(`Conversation ${created.supportCase.reference} opened. ${created.supportCase.deliveryHint}`);
        loadCases();
      } catch (error) {
        setFormError(
          error instanceof ApiError
            ? error.message
            : "We could not open that conversation just now. Nothing was sent — please try again.",
        );
      } finally {
        setBusy(false);
      }
    },
    [tenantId, category, subject, body, loadCases],
  );

  const submitReply = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!tenantId || !detail) return;
      setBusy(true);
      setFormError(null);
      try {
        const updated = await api.support.reply(tenantId, detail.supportCase.id, reply.trim());
        setDetail(updated);
        setReply("");
        setNotice(updated.supportCase.deliveryHint);
        loadCases();
      } catch (error) {
        setFormError(
          error instanceof ApiError
            ? error.message
            : "We could not add that reply just now. Nothing was sent — please try again.",
        );
      } finally {
        setBusy(false);
      }
    },
    [tenantId, detail, reply, loadCases],
  );

  const visibleCases = useMemo(() => {
    if (list.kind !== "ready") return [];
    return showClosed
      ? list.cases.filter((c) => c.state === "RESOLVED" || c.state === "CLOSED")
      : list.cases.filter((c) => c.state !== "RESOLVED" && c.state !== "CLOSED");
  }, [list, showClosed]);

  const moneyWarning = MONEY_CATEGORIES.has(category);
  const canSubmit = subject.trim().length > 0 && body.trim().length > 0 && !busy;

  if (!apiConfigured) {
    return (
      <>
        <PageHeader title="ODIN Support" />
        <ApiUnavailableState detail="The dashboard is not connected to the ODIN API in this build." />
      </>
    );
  }

  return (
    <>
      <PageHeader title="ODIN Support">
        <p>
          Message the ODIN team from inside your workspace. Everything you send here is stored against your
          account, so you and we are always looking at the same conversation.
        </p>
      </PageHeader>

      {notice ? (
        <p role="status" className="support-notice">
          {notice}
        </p>
      ) : null}

      <div className="support-layout">
        <section className="support-list" aria-label="Your support conversations">
          <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
            <Button
              variant={composing ? "secondary" : "primary"}
              type="button"
              onClick={() => {
                setComposing(true);
                setDetail(null);
                setNotice(null);
                setFormError(null);
              }}
            >
              New support conversation
            </Button>
            <Button variant="ghost" type="button" onClick={() => setShowClosed(!showClosed)}>
              {showClosed ? "View open conversations" : "View closed conversations"}
            </Button>
          </div>

          {list.kind === "loading" ? <LoadingState label="Loading your conversations…" /> : null}
          {list.kind === "unavailable" ? (
            <ApiUnavailableState
              detail={
                list.detail === undefined
                  ? "We could not load your conversations. You can still open a new one."
                  : list.detail
              }
            />
          ) : null}
          {list.kind === "ready" && visibleCases.length === 0 ? (
            <EmptyState title={showClosed ? "No closed conversations" : "No open conversations"}>
              {showClosed
                ? "Conversations you and the ODIN team have finished will be kept here."
                : "When you message us, the conversation appears here with its status and the time of the last reply."}
            </EmptyState>
          ) : null}
          {visibleCases.map((c) => (
            <CaseSummary key={c.id} supportCase={c} onOpen={openCase} selected={detail?.supportCase.id === c.id} />
          ))}
        </section>

        <section className="support-detail" aria-label="Conversation">
          {formError ? (
            <p role="alert" className="dryrun-blocked">
              {formError}
            </p>
          ) : null}

          {composing ? (
            <Card
              title="New support conversation"
              description="Tell us what is happening in your own words. A person reads every conversation."
            >
              <form className="form-grid" onSubmit={submitNewCase}>
                <label className="field">
                  <span className="field__label">What is this about?</span>
                  <select
                    className="field__input"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    <option value="">Choose a topic</option>
                    {categories.map((option) => (
                      <option key={option.code} value={option.code}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                {moneyWarning ? (
                  <p className="support-hint">
                    This opens a conversation with our billing team. It does not change your plan, cancel
                    anything, or move any money on its own — those stay separate, deliberate actions you take
                    on your billing page.
                  </p>
                ) : null}

                <label className="field">
                  <span className="field__label">Subject</span>
                  <input
                    className="field__input"
                    value={subject}
                    maxLength={SUBJECT_MAX}
                    onChange={(e) => setSubject(e.target.value)}
                    required
                  />
                </label>
                <label className="field">
                  <span className="field__label">How can we help?</span>
                  <textarea
                    className="field__input"
                    rows={7}
                    value={body}
                    maxLength={BODY_MAX}
                    onChange={(e) => setBody(e.target.value)}
                    required
                  />
                </label>
                <p className="support-hint">
                  Please do not include passwords, card numbers or bot tokens. We never need them, and a
                  support conversation is not the place for them.
                </p>
                <div className="row">
                  <Button variant="primary" type="submit" disabled={!canSubmit}>
                    {busy ? "Sending…" : "Send to ODIN Support"}
                  </Button>
                  <Button variant="ghost" type="button" onClick={() => setComposing(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          ) : null}

          {detailLoading ? <LoadingState label="Opening conversation…" /> : null}

          {!composing && !detailLoading && detail ? (
            <Card title={detail.supportCase.subject} description={detail.supportCase.stateHint}>
              <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
                <StatusIndicator tone={caseTone(detail.supportCase.state)} label={detail.supportCase.stateLabel} />
                <span className="count-chip">{detail.supportCase.categoryLabel}</span>
                <span className="count-chip">{detail.supportCase.reference}</span>
              </div>
              <dl className="kv-grid">
                <div className="kv">
                  <span className="kv__k">Opened</span>
                  <span className="kv__v">{relativeTime(detail.supportCase.createdAtIso)}</span>
                </div>
                <div className="kv">
                  <span className="kv__k">Last reply</span>
                  <span className="kv__v">{relativeTime(detail.supportCase.lastMessageAtIso)}</span>
                </div>
                <div className="kv">
                  <span className="kv__k">Handled by</span>
                  <span className="kv__v">{detail.supportCase.desk}</span>
                </div>
              </dl>
              <p className="support-hint">{detail.supportCase.deliveryHint}</p>

              <div className="support-thread">
                {detail.messages.map((message) => (
                  <MessageBubble key={message.id} message={message} />
                ))}
              </div>

              {detail.supportCase.canReply ? (
                <form className="form-grid" onSubmit={submitReply}>
                  <label className="field">
                    <span className="field__label">Reply to support</span>
                    <textarea
                      className="field__input"
                      rows={4}
                      value={reply}
                      maxLength={BODY_MAX}
                      onChange={(e) => setReply(e.target.value)}
                      required
                    />
                  </label>
                  <div className="row">
                    <Button variant="primary" type="submit" disabled={reply.trim().length === 0 || busy}>
                      {busy ? "Sending…" : "Send reply"}
                    </Button>
                  </div>
                </form>
              ) : (
                <p className="support-hint">
                  This conversation is closed. If it comes back, open a new conversation and mention{" "}
                  {detail.supportCase.reference} — we will still have the history.
                </p>
              )}
            </Card>
          ) : null}

          {!composing && !detailLoading && !detail ? (
            <Card title="Pick a conversation, or start a new one">
              <p>
                Billing, refunds, Discord connection problems, bot permissions, BALDER, or anything else — it
                all comes to the same team.
              </p>
              {/* No email address here on purpose: this IS the support channel now.
                  The addresses that must remain for legal, security and account-recovery
                  reasons are documented in docs/support/SUPPORT_EMAIL_RETENTION.md and
                  are unaffected by this screen. */}
            </Card>
          ) : null}
        </section>
      </div>
    </>
  );
}
